import './App.css';

const stats = [
  { label: 'Students prepped before lab', value: '92%' },
  { label: 'Industry-aligned courses', value: '5' },
  { label: 'On-demand podcasts', value: '19' },
  { label: 'Minutes of guided Interactive video', value: '803' }
];

const differentiators = [
  {
    title: 'Coach Nova AI',
    description:
      'Students get mechanic-friendly answers, practice scenarios, and instant feedback while they study.'
  },
  {
    title: 'Adaptive Curriculum',
    description:
      'Foundation videos, shop-floor podcasts, and advanced pathways build mastery step by step.'
  },
  {
    title: 'Progress That Travels',
    description:
      'iOS, Android, or shop tablets — progress syncs automatically so learners pick up right where they stopped.'
  }
];

const learningJourney = [
  {
    heading: 'Level 1 · High Voltage Safety',
    details: 'Lock-out/tag-out, PPE, hazard boundaries, and emergency response drills.'
  },
  {
    heading: 'Level 1 · Electrical Fundamentals',
    details: 'Meters, diagrams, and troubleshooting labs that mirror ASE-style tasks.'
  },
  {
    heading: 'Level 2 · EV Components',
    details: 'CAN diagnostics, traction motors, and high-voltage architecture deep dives.'
  },
  {
    heading: 'Level 2 · Charging Systems',
    details: 'AC vs. DC fast charging, battery management, and thermal strategies.'
  },
  {
    heading: 'Capstone · Technician Pathway',
    details: 'Certification-ready checklists, instructor sign-off, and career playbooks.'
  }
];

const instructorHighlights = [
  {
    title: 'See Readiness at a Glance',
    description:
      'Dashboards flag who has cleared prerequisites, who is stuck, and where lab time should go next.'
  },
  {
    title: 'Automated Certificates',
    description:
      'Students earn digital certificates when video labs and assessments hit completion thresholds.'
  },
  {
    title: 'Secure Class Onboarding',
    description:
      'Teacher and class codes spin up a cohort in minutes with district-ready reporting and exportable data.'
  }
];

const contactDetails = [
  { label: 'Email', value: 'info@wattworks.ai' },
  { label: 'Website', value: 'wattworks.ai' },
  { label: 'Platforms', value: 'Available on iOS & Android' }
];

function App() {
  return (
    <div className="page">
      <header className="hero">
        <nav className="nav">
          <div className="nav-brand">
            <img src="/logo.jpg" alt="WattWorks logo" className="brand-mark" />
            <div>
              <span className="brand-title">WattWorks</span>
              <span className="brand-subtitle">Hands-on skills for tomorrow&apos;s EV techs</span>
            </div>
          </div>
          <a className="nav-cta" href="#contact">Request a demo</a>
        </nav>

        <div className="hero-content">
          <p className="hero-kicker">Pre-certify every student before they touch the lab.</p>
          <h1>
            Reskill for the
            <span className="hero-highlight">AI-powered workforce.</span>
          </h1>
          <p className="hero-copy">
            WattWorks helps schools and apprenticeships retrain workers at the speed of innovation — starting with
            tomorrow’s Electric Vehicle technicians.
          </p>
          <div className="hero-actions">
            <a className="btn primary" href="#contact">Request a demo</a>
            <a className="btn ghost" href="#journey">See EV pathways</a>
          </div>
        </div>
      </header>

      <section className="stats">
        {stats.map(({ label, value }) => (
          <div key={label} className="stat">
            <span className="stat-value">{value}</span>
            <span className="stat-label">{label}</span>
          </div>
        ))}
      </section>

      <main>
        <section className="section" id="why">
          <div className="section-heading">
            <h2>Built for hands-on jobs in an AI world.</h2>
            <p>
              Automation is transforming technical work faster than traditional training can keep up. WattWorks blends AI
              coaching, adaptive curricula, and shop-floor video labs so students master emerging systems before they
              reach the lab.
            </p>
          </div>
          <div className="highlight-box">
            <ul className="highlight-list">
              <li className="highlight-item highlight-item--primary">
                First focus: Electric Vehicle Systems — the foundation of next-generation mobility.
              </li>
              <li className="highlight-item highlight-item--secondary">
                Next: Data Center and Energy Infrastructure pathways.
              </li>
            </ul>
          </div>
          <div className="card-grid">
            {differentiators.map(({ title, description }) => (
              <article key={title} className="card">
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section muted" id="journey">
          <div className="section-heading">
            <h2>The learning journey</h2>
            <p>
              Our EV curriculum is the first in a growing library of high-demand pathways — engineered with industry
              experts to match certification and employer standards.
            </p>
          </div>
          <div className="journey">
            {learningJourney.map(({ heading, details }) => (
              <div key={heading} className="journey-step">
                <div className="journey-marker" />
                <div className="journey-content">
                  <h3>{heading}</h3>
                  <p>{details}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="journey-footer">
            Coming soon: Data Center Technician, Smart Grid Systems, and Clean Manufacturing Fundamentals.
          </div>
        </section>

        <section className="section" id="why-this-matters">
          <div className="section-heading">
            <h2>Jobs are changing faster than schools can adapt.</h2>
            <p>
              AI is moving five times faster than the internet did — and entry-level roles are disappearing even faster.
              WattWorks helps educators and workforce boards close that gap by turning re-education into an agile,
              data-driven process.
            </p>
          </div>
        </section>

        <section className="section split" id="experience">
          <div className="section-heading">
            <h2>What students experience</h2>
            <p>
              Every WattWorks module uses an adaptive AI framework that helps instructors scale training across multiple
              disciplines while automating progress tracking, certifications, and learner feedback.
            </p>
          </div>
          <div className="experience">
            <div className="experience-card">
              <h3>AI-powered support</h3>
              <p>
                Coach Nova speaks shop-talk, offers safety reminders, and walks students through diagnostics using real
                vehicle scenarios.
              </p>
            </div>
            <div className="experience-card">
              <h3>Dual progression</h3>
              <p>
                Learners level up with XP, streaks, and achievements while unlocking official certificates approved by
                their instructor.
              </p>
            </div>
            <div className="experience-card">
              <h3>Any device, any moment</h3>
              <p>
                Continue Watching keeps videos and podcasts in sync so students can study in the shop, on the bus, or at
                home.
              </p>
            </div>
          </div>
        </section>

        <section className="section muted" id="teachers">
          <div className="section-heading">
            <h2>EV to Data Center: One AI Foundation.</h2>
            <p>
              Every new pathway builds on the same architecture: interactive media, AI-powered feedback, and
              certification dashboards. As industries evolve, so do our modules — turning today’s success with EV
              training into tomorrow’s blueprint for global reskilling.
            </p>
          </div>
          <div className="card-grid">
            {instructorHighlights.map(({ title, description }) => (
              <article key={title} className="card inset">
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
          <div className="callout">
            <p>
              Device registration, class codes, roster exports, and certificate approvals are all powered by the same
              backend that runs the WattWorks mobile apps.
            </p>
          </div>
        </section>

        <section className="section" id="contact">
          <div className="section-heading">
            <h2>Let’s build the workforce of the AI age.</h2>
            <p>
              See how WattWorks accelerates readiness in EV training today — and prepares your program to evolve with
              every new technology tomorrow.
            </p>
          </div>
          <div className="contact-panel">
            <div className="contact-details">
              {contactDetails.map(({ label, value }) => (
                <div key={label} className="detail">
                  <span className="detail-label">{label}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
            </div>
            <div className="contact-cta">
              <p className="contact-copy">
                We&apos;ll walk through live dashboards, sample student journeys, and transition plans for expanding beyond
                EV training into additional high-demand pathways.
              </p>
              <div className="app-badges">
                <a
                  className="store-badge app-store"
                  href="https://www.apple.com/app-store/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                  />
                </a>
                <a
                  className="store-badge play-store"
                  href="https://play.google.com/store"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                  />
                </a>
              </div>
              <a className="btn primary" href="mailto:info@wattworks.ai?subject=Book%20a%20WattWorks%20walkthrough">
                Book a walkthrough
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} WattWorks · Backed by Evidence Ventures.</p>
      </footer>
    </div>
  );
}

export default App;
